/**
 * usePageTracking Hook
 *
 * Automatically tracks page views for SPA navigation using React Router.
 * Call this hook once at the app root level.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../lib/analytics';
import { featureFlags } from '../lib/featureFlags';

type PageType = 'marketing' | 'dashboard' | 'staging' | 'auth' | 'project' | 'profile' | 'other';

/**
 * Determine the page type based on pathname.
 * Used for segmenting analytics by section of the app.
 */
function getPageType(pathname: string): PageType {
  // Marketing pages
  if (pathname === '/' || pathname.startsWith('/virtual-staging') || pathname.startsWith('/partners')) {
    return 'marketing';
  }

  // Dashboard
  if (pathname === '/dashboard') {
    return 'dashboard';
  }

  // Project view
  if (pathname.startsWith('/project/')) {
    return 'project';
  }

  // Staging-related pages
  if (pathname === '/editor' || pathname === '/styles') {
    return 'staging';
  }

  // Auth pages
  if (pathname === '/auth' || pathname.startsWith('/auth/') || pathname === '/reset-password') {
    return 'auth';
  }

  // Profile
  if (pathname === '/profile') {
    return 'profile';
  }

  return 'other';
}

/**
 * Get a human-readable page title based on pathname.
 */
function getPageTitle(pathname: string): string {
  const routes: Record<string, string> = {
    '/': 'Home',
    '/dashboard': 'Dashboard',
    '/profile': 'Profile',
    '/styles': 'Style Library',
    '/editor': 'Editor',
    '/auth': 'Sign In',
    '/reset-password': 'Reset Password',
    '/virtual-staging': 'Virtual Staging',
    '/virtual-staging/pricing': 'Pricing',
    '/virtual-staging/styles': 'Styles',
    '/virtual-staging/real-estate-agents': 'For Real Estate Agents',
    '/virtual-staging/photographers': 'For Photographers',
    '/virtual-staging/broker-teams': 'For Broker Teams',
    '/virtual-staging/before-after': 'Before & After',
    '/partners/photographers': 'Photographer Partners',
    '/partners/brokerages': 'Brokerage Partners',
    '/privacy': 'Privacy Policy',
    '/terms': 'Terms of Service',
  };

  // Check exact match first
  if (routes[pathname]) {
    return routes[pathname];
  }

  // Check prefixes for dynamic routes
  if (pathname.startsWith('/project/')) {
    return 'Project View';
  }
  if (pathname.startsWith('/virtual-staging/styles/')) {
    return 'Style Detail';
  }
  if (pathname.startsWith('/auth/')) {
    return 'Authentication';
  }

  // Fallback to document title or app name
  return document.title || 'VirtualStagePro';
}

/**
 * Hook that tracks page views on route changes.
 * Should be called once at the root of the app (inside Router context).
 */
export function usePageTracking(): void {
  const location = useLocation();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    // Skip if analytics is disabled
    if (!featureFlags.analytics) return;

    // Avoid duplicate tracking on initial mount with same path
    if (previousPath.current === location.pathname) {
      return;
    }

    previousPath.current = location.pathname;

    // Small delay to ensure page has rendered and document title might have updated
    const timeoutId = setTimeout(() => {
      trackPageView({
        pagePath: location.pathname + location.search,
        pageTitle: getPageTitle(location.pathname),
        pageType: getPageType(location.pathname),
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search]);
}
